import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '@/lib/utils';
import { produksjonAPI } from '../../services/api';
import TalentMalEditor from './TalentMalEditor';
import PlanMalEditor from './PlanMalEditor';
import OppmoteMalEditor from './OppmoteMalEditor';
import ConfirmDialog from '../common/ConfirmDialog';

interface ProduksjonsKategori {
  id: number;
  navn: string;
  beskrivelse: string | null;
  plassering?: string | null;
}

const ProduksjonsKategoriMal: React.FC = () => {
  const [kategorier, setKategorier] = useState<ProduksjonsKategori[]>([]);
  const [selectedKategori, setSelectedKategori] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // Høyrepanel felter
  const [navn, setNavn] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [plassering, setPlassering] = useState('');
  const [activeTab, setActiveTab] = useState<'talenter' | 'oppmote' | 'plan'>('talenter');

  // Ny kategori dialog
  const [newKategoriOpen, setNewKategoriOpen] = useState(false);
  const [newKategoriNavn, setNewKategoriNavn] = useState('');
  const [newKategoriBeskrivelse, setNewKategoriBeskrivelse] = useState('');
  const [newKategoriPlassering, setNewKategoriPlassering] = useState('');
  // Feil ved opprettelse vises i dialogen, ikke i sidens felles error bak den
  const [nyMalFeil, setNyMalFeil] = useState<string | null>(null);
  // Sperrer «Opprett» mens kallet pågår. Et dobbeltklikk ville ellers gitt et andre kall som
  // feiler med duplikat etter at dialogen er lukket, og meldingen ville stått der neste gang.
  const [oppretter, setOppretter] = useState(false);

  // Slett kategori confirm
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const kategoriData = await produksjonAPI.getAllKategorier();
      setKategorier(kategoriData.kategorier);
    } catch (err: any) {
      setError('Kunne ikke laste data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKategoriClick = (id: number) => {
    // Toggle: klikk på samme kategori lukker høyrepanelet
    setSelectedKategori((prev) => (prev === id ? null : id));
    setActiveTab('plan');

    // Prefill felter fra valgt kategori
    const k = kategorier.find((k) => k.id === id);
    if (k) {
      setNavn(k.navn || '');
      setBeskrivelse(k.beskrivelse || '');
      setPlassering(k.plassering || '');
      setSaved(null);
      setError(null);
    }
  };

  // Lukking med Avbryt, Escape, klikk utenfor og krysset går alle hit
  const endreNyMalApen = (apen: boolean) => {
    setNewKategoriOpen(apen);
    if (!apen) setNyMalFeil(null);
  };

  const handleCreateKategori = async () => {
    try {
      setNyMalFeil(null);
      setOppretter(true);
      const { kategori } = await produksjonAPI.createKategori({
        navn: newKategoriNavn,
        beskrivelse: newKategoriBeskrivelse || undefined,
        plassering: newKategoriPlassering || undefined,
      });

      setError(null);
      setSaved(`Produksjonsmal "${kategori.navn}" opprettet!`);
      await fetchData();

      // Reset og lukk dialog
      setNewKategoriNavn('');
      setNewKategoriBeskrivelse('');
      setNewKategoriPlassering('');
      setNewKategoriOpen(false);

      // Velg den nye kategorien og oppdater felter
      setSelectedKategori(kategori.id);
      setNavn(kategori.navn);
      setBeskrivelse(kategori.beskrivelse || '');
      setPlassering(kategori.plassering || '');
      setActiveTab('plan');
    } catch (err: any) {
      // Dialogen og feltene blir stående, så brukeren kan rette navnet
      setNyMalFeil(err?.response?.data?.error || 'Kunne ikke opprette produksjonsmal');
      console.error(err);
    } finally {
      setOppretter(false);
    }
  };

  const handleDeleteKategori = async () => {
    if (!selectedKategori) return;

    try {
      setError(null);
      const kategoriNavn = kategorier.find((k) => k.id === selectedKategori)?.navn;

      await produksjonAPI.deleteKategori(selectedKategori, true); // deep=true for å slette maler også

      setSaved(`Produksjonsmal "${kategoriNavn}" slettet!`);
      setSelectedKategori(null);
      setDeleteConfirmOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke slette kategori');
      console.error(err);
      setDeleteConfirmOpen(false);
    }
  };

  const handleLagreKategori = async () => {
    if (!selectedKategori) return;
    try {
      setError(null);
      setSaved(null);
      await produksjonAPI.updateKategori(selectedKategori, { navn, beskrivelse, plassering });
      // Refetch og hold valg
      const data = await produksjonAPI.getAllKategorier();
      setKategorier(data.kategorier);
      const k = data.kategorier.find((x: any) => x.id === selectedKategori);
      if (k) {
        setNavn(k.navn || '');
        setBeskrivelse(k.beskrivelse || '');
        setPlassering(k.plassering || '');
      }
      setSaved('Kategori lagret');
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.error || 'Kunne ikke lagre kategori');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-sm underline">
              Lukk
            </button>
          </AlertDescription>
        </Alert>
      )}
      {saved && (
        <Alert className="mb-4 border-status-confirmed/40 bg-status-confirmed/10">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{saved}</span>
            <button onClick={() => setSaved(null)} className="text-sm underline">
              Lukk
            </button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-6">
        {/* Venstre: Produksjonsmaler */}
        <div className="rounded-lg border bg-card p-4 h-fit md:sticky md:top-4">
          <Button className="w-full mb-4" onClick={() => setNewKategoriOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Ny mal
          </Button>
          <div className="space-y-1">
            {kategorier.map((kategori) => {
              const isSelected = selectedKategori === kategori.id;
              return (
                <button
                  key={kategori.id}
                  onClick={() => handleKategoriClick(kategori.id)}
                  className={cn(
                    'w-full flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left transition-colors',
                    isSelected ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'hover:bg-muted'
                  )}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{kategori.navn}</span>
                    {kategori.beskrivelse && (
                      <span
                        className={cn(
                          'block text-xs truncate',
                          isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        )}
                      >
                        {kategori.beskrivelse}
                      </span>
                    )}
                  </span>
                  {isSelected ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Høyre: Kategori-detaljer + faner */}
        <div className="rounded-lg border bg-card p-6">
          {selectedKategori ? (
            <>
              {/* Kategori header-felter */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-end">
                <div className="space-y-1.5">
                  <Label>Navn</Label>
                  <Input value={navn} onChange={(e) => setNavn(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Beskrivelse</Label>
                  <Input value={beskrivelse} onChange={(e) => setBeskrivelse(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Plassering</Label>
                  <Input
                    placeholder="F.eks. Scene, Backstage"
                    value={plassering}
                    onChange={(e) => setPlassering(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-between mb-4">
                <Button variant="outline" className="text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteConfirmOpen(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Slett kategori
                </Button>
                <Button onClick={handleLagreKategori}>Lagre</Button>
              </div>

              <ConfirmDialog
                open={deleteConfirmOpen}
                title="Slett produksjonsmal"
                message={`Er du sikker på at du vil slette produksjonsmalen "${navn}"? Dette vil også slette alle tilknyttede innslag (plan, bemanning, oppmøtetider). Dette kan ikke angres.`}
                confirmText="Slett"
                cancelText="Avbryt"
                destructive
                onCancel={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDeleteKategori}
              />

              {/* Faner */}
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="mb-4">
                <TabsList>
                  <TabsTrigger value="plan">Plan</TabsTrigger>
                  <TabsTrigger value="talenter">Bemanning</TabsTrigger>
                  <TabsTrigger value="oppmote">Oppmøtetider</TabsTrigger>
                </TabsList>
              </Tabs>

              {activeTab === 'plan' && (
                <PlanMalEditor
                  key={`plan-${selectedKategori}`}
                  kategoriId={selectedKategori}
                  onSave={() => {
                    setSaved('Plan-mal oppdatert');
                    setTimeout(() => setSaved(null), 3000);
                  }}
                />
              )}

              {activeTab === 'talenter' && (
                <TalentMalEditor
                  key={`talenter-${selectedKategori}`}
                  kategoriId={selectedKategori}
                  kategoriNavn={navn}
                  onSave={() => {}}
                />
              )}

              {activeTab === 'oppmote' && (
                <OppmoteMalEditor
                  key={`oppmote-${selectedKategori}`}
                  kategoriId={selectedKategori}
                  onSave={() => {
                    setSaved('Oppmøte-mal oppdatert');
                    setTimeout(() => setSaved(null), 3000);
                  }}
                />
              )}
            </>
          ) : (
            <Alert>
              <AlertDescription>
                Velg en produksjonsmal fra listen til venstre for å konfigurere detaljer
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Dialog for ny mal */}
      <Dialog open={newKategoriOpen} onOpenChange={endreNyMalApen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Opprett ny produksjonsmal</DialogTitle>
          </DialogHeader>
          {nyMalFeil && (
            <Alert variant="destructive">
              <AlertDescription>{nyMalFeil}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label>Navn *</Label>
              <Input
                value={newKategoriNavn}
                onChange={(e) => setNewKategoriNavn(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Beskrivelse</Label>
              <Textarea
                value={newKategoriBeskrivelse}
                onChange={(e) => setNewKategoriBeskrivelse(e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Plassering</Label>
              <Input
                placeholder="F.eks. Hovedscenen, Backstage"
                value={newKategoriPlassering}
                onChange={(e) => setNewKategoriPlassering(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => endreNyMalApen(false)}>
              Avbryt
            </Button>
            <Button onClick={handleCreateKategori} disabled={!newKategoriNavn.trim() || oppretter}>
              Opprett
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProduksjonsKategoriMal;
